<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { API_BASE_URL, selectedAdmin, getAuthToken } from '@/stores/appConfig'
import { templates } from '@/stores/websiteTemplatesStore'
import ImagePickerModal from '@/components/builder/ImagePickerModal.vue'
import {
  builderContent,
  content,
} from '@/stores/websiteBuilderStore'
import { compilePreviewComponent } from '@/utils/templatePreview'


const router = useRouter()

// ── Raw data ──────────────────────────────────────────────────────────────
const users     = ref([])
const companies = ref([])
const websites  = ref([])
const admins    = ref([])
const loading   = ref(false)
const loadError = ref('')

// ── User list state ───────────────────────────────────────────────────────
const searchQuery = ref('')
const selectedUid = ref(null)
const showAll     = ref(false)
const isSearching = computed(() => searchQuery.value.trim().length > 0)

// ── Company search state ──────────────────────────────────────────────────
const companySearch = ref('')

const listUsers = computed(() => {
  const q    = searchQuery.value.trim().toLowerCase()
  // search always covers all users; toggle only applies when idle
  const pool = (q || showAll.value)
    ? users.value
    : users.value.filter(u => u.role === 'owner')
  if (!q) return pool
  return pool.filter(u =>
    displayName(u).toLowerCase().includes(q) ||
    (u.email || '').toLowerCase().includes(q) ||
    (companyFor(u)?.company_name || '').toLowerCase().includes(q)
  )
})

// ── Selected user's joined data ───────────────────────────────────────────
const selectedUser = computed(() =>
  users.value.find(u => u.firebase_uid === selectedUid.value) || null
)
const selectedCompany = computed(() =>
  selectedUser.value?.company_id
    ? companies.value.find(c => c.company_id === selectedUser.value.company_id) || null
    : null
)
const selectedWebsite = computed(() =>
  selectedUser.value?.company_id
    ? websites.value.find(w => w.company_id === selectedUser.value.company_id) || null
    : null
)
const selectedTeam = computed(() =>
  selectedUser.value?.company_id
    ? users.value.filter(u =>
        u.company_id === selectedUser.value.company_id &&
        u.firebase_uid !== selectedUid.value
      )
    : []
)

// ── Company list ──────────────────────────────────────────────────────────
const listCompanies = computed(() => {
  const q = companySearch.value.trim().toLowerCase()
  if (!q) return companies.value
  return companies.value.filter(c => {
    const owner = ownerFor(c)
    return (
      (c.company_name  || '').toLowerCase().includes(q) ||
      (c.industry      || '').toLowerCase().includes(q) ||
      (c.company_email || '').toLowerCase().includes(q) ||
      (owner ? displayName(owner) : '').toLowerCase().includes(q) ||
      (owner?.email || '').toLowerCase().includes(q)
    )
  })
})

// ── Helpers ───────────────────────────────────────────────────────────────
function displayName(u) {
  const n = [u?.first_name, u?.last_name].filter(Boolean).join(' ')
  return n || u?.email || u?.firebase_uid || '—'
}

function initials(u) {
  const f = u?.first_name?.[0] || ''
  const l = u?.last_name?.[0] || ''
  return (f + l).toUpperCase() || (u?.email?.[0] || '?').toUpperCase()
}

function companyFor(u) {
  return companies.value.find(c => c.company_id === u?.company_id) || null
}

function ownerFor(c) {
  return users.value.find(u => u.company_id === c?.company_id && u.role === 'owner') || null
}

function websiteFor(c) {
  return websites.value.find(w => w.company_id === c?.company_id) || null
}

function formatDate(str) {
  if (!str) return '—'
  return new Date(str).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
}

function truncate(str, len = 26) {
  if (!str) return '—'
  return str.length > len ? str.slice(0, len) + '…' : str
}

// ── Edit dialog ───────────────────────────────────────────────────────────
const editDialog  = ref(false)
const editSection = ref('')
const editTarget  = ref(null)
const editForm    = ref({})
const editSaving  = ref(false)
const editError   = ref('')

function openEditUser(u) {
  editSection.value = 'user'
  editTarget.value  = u
  editForm.value    = { ...u }
  editError.value   = ''
  editDialog.value  = true
}

function openEditCompany(c) {
  editSection.value = 'company'
  editTarget.value  = c
  editForm.value    = { ...c }
  editError.value   = ''
  editDialog.value  = true
}

async function saveEdit() {
  editSaving.value = true
  editError.value  = ''
  try {
    const token = await getAuthToken()
    let url, body

    if (editSection.value === 'user') {
      url  = `${API_BASE_URL}/admin/users/${editTarget.value.firebase_uid}`
      body = {
        first_name: editForm.value.first_name,
        last_name:  editForm.value.last_name,
        email:      editForm.value.email,
        role:       editForm.value.role,
      }
    } else {
      url  = `${API_BASE_URL}/admin/companies/${editTarget.value.company_id}`
      body = {
        company_name:      editForm.value.company_name,
        company_email:     editForm.value.company_email,
        company_phone:     editForm.value.company_phone,
        industry:          editForm.value.industry,
        sub_industry:      editForm.value.sub_industry,
        main_contact_name: editForm.value.main_contact_name,
      }
    }

    const res  = await fetch(url, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body:    JSON.stringify(body),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Save failed.')

    editDialog.value = false
    await loadAll()
  } catch (err) {
    editError.value = err.message
  } finally {
    editSaving.value = false
  }
}

// ── Delete dialog ─────────────────────────────────────────────────────────
const deleteDialog   = ref(false)
const deleteSection  = ref('')
const deleteTarget   = ref(null)
const deleteDeleting = ref(false)
const deleteError    = ref('')

function openDeleteUser(u) {
  deleteSection.value = 'user'
  deleteTarget.value  = u
  deleteError.value   = ''
  deleteDialog.value  = true
}

function openDeleteAdmin(a) {
  deleteSection.value = 'admin'
  deleteTarget.value  = a
  deleteError.value   = ''
  deleteDialog.value  = true
}

async function confirmDelete() {
  deleteDeleting.value = true
  deleteError.value    = ''
  try {
    const token    = await getAuthToken()
    const uid      = deleteTarget.value.firebase_uid
    const endpoint = deleteSection.value === 'user'
      ? `${API_BASE_URL}/admin/users/${uid}`
      : `${API_BASE_URL}/admin/admins/${uid}`

    const res  = await fetch(endpoint, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Delete failed.')

    deleteDialog.value = false
    if (deleteSection.value === 'user') {
      if (selectedUid.value === uid) selectedUid.value = null
      await loadAll()
    } else {
      await loadAdmins()
    }
  } catch (err) {
    deleteError.value = err.message
  } finally {
    deleteDeleting.value = false
  }
}

// ── Add Admin dialog ──────────────────────────────────────────────────────
const addAdminDialog = ref(false)
const addAdminForm   = ref({ firebase_uid: '', email: '', first_name: '', last_name: '' })
const addAdminSaving = ref(false)
const addAdminError  = ref('')

async function submitAddAdmin() {
  addAdminSaving.value = true
  addAdminError.value  = ''
  try {
    const token = await getAuthToken()
    const res   = await fetch(`${API_BASE_URL}/admin/admins`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body:    JSON.stringify(addAdminForm.value),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to add admin.')

    addAdminDialog.value = false
    addAdminForm.value   = { firebase_uid: '', email: '', first_name: '', last_name: '' }
    await loadAdmins()
  } catch (err) {
    addAdminError.value = err.message
  } finally {
    addAdminSaving.value = false
  }
}

// ── Admins table ──────────────────────────────────────────────────────────
const adminRows = computed(() =>
  admins.value.map(a => ({
    _raw:          a,
    'Firebase UID': a.firebase_uid || '',
    Email:          a.email        || '',
    'First Name':   a.first_name   || '',
    'Last Name':    a.last_name    || '',
    Created:        a.created_at   || '',
  }))
)

// ── Data loaders ──────────────────────────────────────────────────────────
async function loadAll() {
  loading.value   = true
  loadError.value = ''
  try {
    const token = await getAuthToken()
    const h     = { Authorization: `Bearer ${token}` }
    const [ur, cr, wr] = await Promise.all([
      fetch(`${API_BASE_URL}/admin/users`,     { headers: h }),
      fetch(`${API_BASE_URL}/admin/companies`, { headers: h }),
      fetch(`${API_BASE_URL}/admin/websites`,  { headers: h }),
    ])
    const [ud, cd, wd] = await Promise.all([ur.json(), cr.json(), wr.json()])
    users.value     = ud.users     || []
    companies.value = cd.companies || []
    websites.value  = wd.websites  || []
  } catch (err) {
    loadError.value = err.message
  } finally {
    loading.value = false
  }
}

async function loadAdmins() {
  loading.value   = true
  loadError.value = ''
  try {
    const token = await getAuthToken()
    const res   = await fetch(`${API_BASE_URL}/admin/admins`, { headers: { Authorization: `Bearer ${token}` } })
    const data  = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load admins.')
    admins.value = data.admins || []
  } catch (err) {
    loadError.value = err.message
  } finally {
    loading.value = false
  }
}

function manageCMS(companyId) {
  router.push({ path: '/cms/builder', query: { company_id: companyId } })
}

// ── Activity logs (per-user) ───────────────────────────────────────────────
const activityLogs    = ref([])
const activityLoading = ref(false)
const activityError   = ref('')

async function loadActivityLogs(uid) {
  if (!uid) { activityLogs.value = []; return }
  activityLoading.value = true
  activityError.value   = ''
  try {
    const token = await getAuthToken()
    const res   = await fetch(`${API_BASE_URL}/admin/logs?firebase_uid=${uid}&limit=20`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load activity.')
    activityLogs.value = data.logs || []
  } catch (err) {
    activityError.value = err.message
  } finally {
    activityLoading.value = false
  }
}

watch(selectedUid, uid => {
  if (selectedAdmin.value === '/admin/users') loadActivityLogs(uid)
})

// ── Global logs tab ───────────────────────────────────────────────────────
// ── Template Builder ──────────────────────────────────────
const PREVIEW_ID            = '__admin_preview__'
const ADMIN_ASSET_COMPANY_ID = 'quotastic-admin-assets'
const templatePasteInput    = ref('')
const templateParsed        = ref(null)
const templateOriginalId    = ref('')
const templateParseError    = ref('')
const showTemplatePreview   = ref(false)
const templatePreviewIframe = ref(null)
const templateSaving        = ref(false)
const templateSaveError       = ref('')
const templateSaveSuccess     = ref('')
const templateThumbnailUrl    = ref('')
const templateIdInput         = ref('')
const templateNameInput       = ref('')
const templateCategoryInput   = ref('')

// All templates from DB (including drafts) — loaded when the Templates tab is opened.
const allDbTemplates        = ref([])
const allDbTemplatesLoading = ref(false)
const allDbTemplatesError   = ref('')
const selectedLoadId        = ref('')

async function loadAllDbTemplates() {
  if (allDbTemplatesLoading.value) return
  allDbTemplatesLoading.value = true
  allDbTemplatesError.value   = ''
  try {
    const token = await getAuthToken()
    const res   = await fetch(`${API_BASE_URL}/admin/templates`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data  = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load templates.')
    allDbTemplates.value = data.templates || []
  } catch (e) {
    allDbTemplatesError.value = e.message || 'Failed to load templates.'
    console.warn('[admin] loadAllDbTemplates:', e.message)
  } finally {
    allDbTemplatesLoading.value = false
  }
}

watch(selectedAdmin, (val) => {
  if (val === '/admin/templates' && !allDbTemplates.value.length && !allDbTemplatesLoading.value) {
    loadAllDbTemplates()
  }
  if (val === '/admin/print-templates' && !printTemplatesAdmin.value.length && !printTemplatesLoading.value) {
    loadPrintTemplates()
  }
}, { immediate: true })

// ── Print Templates (Marketing / Physical Marketing) ────────────────────────
const printTemplatesAdmin      = ref([])
const printTemplatesLoading    = ref(false)
const selectedPrintTemplateId  = ref(null)
const printTemplateForm        = ref(null)
const printTemplateIsExisting  = ref(false)
const printTemplateSaving      = ref(false)
const printTemplateError       = ref('')
const printPreviewCssKey       = ref(0)

async function loadPrintTemplates() {
  if (printTemplatesLoading.value) return
  printTemplatesLoading.value = true
  try {
    const token = await getAuthToken()
    const res = await fetch(`${API_BASE_URL}/admin/print-templates`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load print templates.')
    printTemplatesAdmin.value = data.templates || []
  } catch (e) {
    console.warn('[admin] loadPrintTemplates:', e.message)
  } finally {
    printTemplatesLoading.value = false
  }
}

function newPrintTemplate() {
  selectedPrintTemplateId.value = null
  printTemplateIsExisting.value = false
  printTemplateError.value = ''
  printTemplateForm.value = {
    id: '', name: '', category: '', status: 'draft', sort_order: 100,
    width_in: 3.5, height_in: 2, bleed_in: 0.125,
    template: '<div class="card"></div>', css: '', contentJson: '{}',
  }
}

function selectPrintTemplate(id) {
  const t = printTemplatesAdmin.value.find(x => x.id === id)
  if (!t) return
  selectedPrintTemplateId.value = id
  printTemplateIsExisting.value = true
  printTemplateError.value = ''
  printTemplateForm.value = {
    id: t.id,
    name: t.name,
    category: t.category || '',
    status: t.status || 'draft',
    sort_order: t.sort_order ?? 100,
    width_in: t.width_in,
    height_in: t.height_in,
    bleed_in: t.bleed_in,
    template: t.template,
    css: t.css,
    contentJson: JSON.stringify(typeof t.content === 'string' ? JSON.parse(t.content) : t.content, null, 2),
  }
}

async function savePrintTemplate() {
  printTemplateError.value = ''

  let content
  try {
    content = JSON.parse(printTemplateForm.value.contentJson)
  } catch {
    printTemplateError.value = 'Content must be valid JSON.'
    return
  }

  if (!printTemplateForm.value.id.trim() || !printTemplateForm.value.name.trim()) {
    printTemplateError.value = 'ID and name are required.'
    return
  }

  if (!printTemplateForm.value.width_in || !printTemplateForm.value.height_in) {
    printTemplateError.value = 'Width and height are required.'
    return
  }

  printTemplateSaving.value = true
  try {
    const token = await getAuthToken()
    const res = await fetch(`${API_BASE_URL}/admin/print-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        id: printTemplateForm.value.id.trim(),
        name: printTemplateForm.value.name.trim(),
        category: printTemplateForm.value.category || null,
        status: printTemplateForm.value.status,
        sort_order: Number(printTemplateForm.value.sort_order) || 100,
        width_in: Number(printTemplateForm.value.width_in),
        height_in: Number(printTemplateForm.value.height_in),
        bleed_in: Number(printTemplateForm.value.bleed_in) || 0,
        template: printTemplateForm.value.template,
        css: printTemplateForm.value.css,
        content,
      }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to save print template.')
    await loadPrintTemplates()
    selectPrintTemplate(data.template.id)
  } catch (e) {
    printTemplateError.value = e.message || 'Failed to save print template.'
  } finally {
    printTemplateSaving.value = false
  }
}

async function deletePrintTemplate() {
  if (!printTemplateIsExisting.value) return
  if (!confirm(`Delete print template "${printTemplateForm.value.name}"?`)) return

  try {
    const token = await getAuthToken()
    const res = await fetch(`${API_BASE_URL}/admin/print-templates/${printTemplateForm.value.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to delete print template.')
    printTemplateForm.value = null
    selectedPrintTemplateId.value = null
    await loadPrintTemplates()
  } catch (e) {
    printTemplateError.value = e.message || 'Failed to delete print template.'
  }
}

const printPreviewComponent = computed(() => {
  if (!printTemplateForm.value?.template) return null
  try {
    return compilePreviewComponent(printTemplateForm.value.template)
  } catch {
    return null
  }
})

const printPreviewContent = computed(() => {
  try {
    return JSON.parse(printTemplateForm.value?.contentJson || '{}')
  } catch {
    return {}
  }
})

const printPreviewCanvasStyle = computed(() => ({
  width: `${Number(printTemplateForm.value?.width_in) || 3.5}in`,
  height: `${Number(printTemplateForm.value?.height_in) || 2}in`,
}))

watch(() => printTemplateForm.value?.css, () => { printPreviewCssKey.value++ })

function loadExistingTemplate(id) {
  if (!id) return
  const tpl = allDbTemplates.value.find(t => t.id === id)
  if (!tpl) return

  // Populate metadata form fields.
  templateIdInput.value         = tpl.id
  templateNameInput.value       = tpl.name
  templateCategoryInput.value   = tpl.category || ''
  templateThumbnailUrl.value    = tpl.thumbnail || ''

  // Only put structural parts in the textarea — the AI-owned fields.
  const structural = { template: tpl.template, css: tpl.css, content: tpl.content }
  templatePasteInput.value = JSON.stringify(structural, null, 2)

  // Auto-validate so preview is immediately available.
  validateTemplate()
}

function validateTemplate() {
  templateParsed.value     = null
  templateOriginalId.value = ''
  templateParseError.value = ''
  templateSaveError.value  = ''
  templateSaveSuccess.value = ''
  showTemplatePreview.value = false

  try {
    if (!templateIdInput.value.trim())   throw new Error('Template ID is required.')
    if (!templateNameInput.value.trim()) throw new Error('Template name is required.')
    const obj = Function('"use strict"; return (' + templatePasteInput.value + ')')()
    if (!obj?.template) throw new Error('Missing required field: template')
    if (!obj?.css)      throw new Error('Missing required field: css')
    if (!obj?.content)  throw new Error('Missing required field: content')

    templateOriginalId.value = templateIdInput.value.trim()
    const entry = {
      ...obj,
      id:       PREVIEW_ID,
      name:     templateNameInput.value.trim(),
      category: templateCategoryInput.value.trim() || null,
    }
    const existing = templates.findIndex(t => t.id === PREVIEW_ID)
    if (existing >= 0) templates.splice(existing, 1, entry)
    else templates.push(entry)

    templateParsed.value = entry
    showTemplatePreview.value = true
  } catch (e) {
    templateParseError.value = e.message
  }
}

async function saveTemplate(status = 'draft') {
  if (!templateParsed.value) return
  if (!templateIdInput.value.trim() || !templateNameInput.value.trim()) {
    templateSaveError.value = 'Template ID and name are required.'
    return
  }
  templateSaving.value     = true
  templateSaveError.value  = ''
  templateSaveSuccess.value = ''
  try {
    const token = await getAuthToken()
    const { template, css, content } = templateParsed.value
    const body = {
      id:       templateIdInput.value.trim(),
      name:     templateNameInput.value.trim(),
      category: templateCategoryInput.value.trim() || null,
      thumbnail: templateThumbnailUrl.value || null,
      template,
      css,
      content,
      status,
    }
    const res  = await fetch(`${API_BASE_URL}/admin/templates`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body:    JSON.stringify(body),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Save failed.')
    templateSaveSuccess.value = `Saved as "${status}" — id: ${templateIdInput.value.trim()}`
    loadAllDbTemplates()
  } catch (e) {
    templateSaveError.value = e.message
  } finally {
    templateSaving.value = false
  }
}

function onPreviewLoad() {
  if (!templateParsed.value) return
  const { template, css, content } = JSON.parse(JSON.stringify(templateParsed.value))
  templatePreviewIframe.value?.contentWindow?.postMessage({
    type: 'quotastic:builder-preview',
    template_id: PREVIEW_ID,
    content,
    website: { template_id: PREVIEW_ID },
    inline_template: { id: PREVIEW_ID, template, css, content }
  }, window.location.origin)
}

function clearTemplate() {
  templatePasteInput.value        = ''
  templateParseError.value        = ''
  templateSaveError.value         = ''
  templateSaveSuccess.value       = ''
  templateThumbnailUrl.value      = ''
  selectedLoadId.value            = ''
  templateIdInput.value           = ''
  templateNameInput.value         = ''
  templateCategoryInput.value     = ''
  templateParsed.value      = null
  templateOriginalId.value  = ''
  showTemplatePreview.value = false
  const i = templates.findIndex(t => t.id === PREVIEW_ID)
  if (i >= 0) templates.splice(i, 1)
}

// ── Database explorer tab ────────────────────────────────────────────────
const dbTables        = ref([])
const dbTablesLoading = ref(false)
const dbTablesError   = ref('')
const selectedDbTable = ref('')
const dbColumns       = ref([])
const dbRows          = ref([])
const dbTotal         = ref(0)
const dbPage          = ref(1)
const dbPageSize      = ref(25)
const dbSort          = ref('')
const dbDir           = ref('asc')
const dbRowsLoading   = ref(false)
const dbError         = ref('')
const expandedDbCell  = ref(null)

const dbTotalPages = computed(() => Math.max(1, Math.ceil(dbTotal.value / dbPageSize.value)))

async function loadDbTables() {
  if (dbTablesLoading.value) return
  dbTablesLoading.value = true
  dbTablesError.value   = ''
  try {
    const token = await getAuthToken()
    const res   = await fetch(`${API_BASE_URL}/admin/database/tables`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load tables.')
    dbTables.value = data.tables || []
  } catch (e) {
    dbTablesError.value = e.message || 'Failed to load tables.'
  } finally {
    dbTablesLoading.value = false
  }
}

async function loadDbTableRows(table, page = 1) {
  if (!table) return
  dbRowsLoading.value = true
  dbError.value        = ''
  expandedDbCell.value = null
  try {
    const token  = await getAuthToken()
    const params = new URLSearchParams({ page: String(page), pageSize: String(dbPageSize.value) })
    if (dbSort.value) { params.set('sort', dbSort.value); params.set('dir', dbDir.value) }
    const res  = await fetch(`${API_BASE_URL}/admin/database/tables/${encodeURIComponent(table)}?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load table rows.')
    dbColumns.value = data.columns || []
    dbRows.value     = data.rows || []
    dbTotal.value    = data.total || 0
    dbPage.value     = data.page || page
  } catch (e) {
    dbError.value = e.message || 'Failed to load table rows.'
    dbColumns.value = []
    dbRows.value    = []
  } finally {
    dbRowsLoading.value = false
  }
}

function selectDbTable(name) {
  selectedDbTable.value = name
  dbSort.value = ''
  dbDir.value  = 'asc'
  loadDbTableRows(name, 1)
}

function sortDbColumn(name) {
  if (dbSort.value === name) {
    dbDir.value = dbDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    dbSort.value = name
    dbDir.value  = 'asc'
  }
  loadDbTableRows(selectedDbTable.value, 1)
}

function changeDbPageSize(size) {
  dbPageSize.value = size
  loadDbTableRows(selectedDbTable.value, 1)
}

function formatDbCell(value, _type) {
  if (value === null || value === undefined) return '—'
  if (value instanceof Date) return value.toLocaleString('en-CA', { hour12: false })
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function looksLikeJson(value) {
  if (typeof value !== 'string' || !value.length) return typeof value === 'object' && value !== null
  const c = value.trim()[0]
  return c === '{' || c === '['
}

function prettyJson(value) {
  try {
    const obj = typeof value === 'string' ? JSON.parse(value) : value
    return JSON.stringify(obj, null, 2)
  } catch {
    return String(value)
  }
}

function dbCellKey(rowIndex, colName) {
  return `${rowIndex}:${colName}`
}

watch(selectedAdmin, (val) => {
  if (val === '/admin/database' && !dbTables.value.length && !dbTablesLoading.value) {
    loadDbTables()
  }
}, { immediate: true })

const globalLogs        = ref([])
const globalLogsLoading = ref(false)
const globalLogsError   = ref('')
const logFilterUid      = ref('')
const logFilterAction   = ref('')
const logFilterFrom     = ref('')
const logFilterTo       = ref('')

async function loadGlobalLogs() {
  globalLogsLoading.value = true
  globalLogsError.value   = ''
  try {
    const token  = await getAuthToken()
    const params = new URLSearchParams({ limit: '100' })
    if (logFilterUid.value)    params.set('firebase_uid', logFilterUid.value)
    if (logFilterAction.value) params.set('action', logFilterAction.value)
    if (logFilterFrom.value)   params.set('from', logFilterFrom.value)
    if (logFilterTo.value)     params.set('to', logFilterTo.value)
    const res  = await fetch(`${API_BASE_URL}/admin/logs?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load logs.')
    globalLogs.value = data.logs || []
  } catch (err) {
    globalLogsError.value = err.message
  } finally {
    globalLogsLoading.value = false
  }
}

function formatRelative(str) {
  if (!str) return '—'
  const diff = Date.now() - new Date(str).getTime()
  const sec  = Math.floor(diff / 1000)
  if (sec < 60)    return 'just now'
  if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
  return `${Math.floor(sec / 86400)}d ago`
}

function actionColor(action) {
  if (!action) return '#667085'
  if (action === 'login')               return '#667085'
  if (action.startsWith('quote.'))      return '#2169c6'
  if (action.startsWith('website.'))    return '#1f5c4d'
  if (action.startsWith('domain.') || action.startsWith('dns.')) return '#7c3aed'
  if (action.startsWith('company.'))    return '#b45309'
  return '#667085'
}

function userFor(uid) {
  return users.value.find(u => u.firebase_uid === uid) || null
}

function refreshActive() {
  if (selectedAdmin.value === '/admin/users' || selectedAdmin.value === '/admin/companies') loadAll()
  else if (selectedAdmin.value === '/admin/logs') loadGlobalLogs()
  else if (selectedAdmin.value === '/admin/database') {
    loadDbTables()
    if (selectedDbTable.value) loadDbTableRows(selectedDbTable.value, dbPage.value)
  }
  else if (selectedAdmin.value === '/admin/templates') loadAllDbTemplates()
  else if (selectedAdmin.value === '/admin/stock-media') loadStockAdminImages()
  else if (selectedAdmin.value === '/admin/domain-reservations') loadDomainReservations()
  else loadAdmins()
}

watch(selectedAdmin, (val) => {
  refreshActive()
  if (val === '/admin/users' && selectedUid.value) loadActivityLogs(selectedUid.value)
}, { immediate: true })








//───────────────────────────────────────────────────────────
// Image Modal
//───────────────────────────────────────────────────────────
const imagePickerOpen = ref(false)
const imagePickerTargetIndex = ref(null)
const imagePickerFieldTarget = ref(null)   // { sectionKey, fieldKey } when opened from a FieldRenderer image field
const imagePickerSelectedUrl = ref('')
const thumbnailPickerMode    = ref(false)  // when true, image selection writes to templateThumbnailUrl
const tenantImages = ref([])
const tenantImagesLoading = ref(false)
const tenantImagesUploading = ref(false)
const tenantImagesUploadError = ref('')
const tenantImageDeleting = ref(null)

// In AdminView the "active company" is whichever user/company is selected in the panel.
const adminCompanyId = computed(() => selectedCompany.value?.company_id || null)

// Falls back to the shared admin asset bucket when editing templates (no user selected).
const effectiveTenantId = computed(() => adminCompanyId.value || ADMIN_ASSET_COMPANY_ID)

async function loadTenantImages() {
  if (!effectiveTenantId.value) return

  tenantImagesLoading.value = true
  tenantImagesUploadError.value = ''

  try {
    const token = await getAuthToken()

    // Swap this endpoint for your real tenant image endpoint.
    const response = await fetch(
      `${API_BASE_URL}/media?company_id=${effectiveTenantId.value}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      }
    )

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      throw new Error(data.error || 'Unable to load images.')
    }

    tenantImages.value = data.images || data.media || []
  }
  catch (error) {
    console.error('loadTenantImages error:', error)
    tenantImagesUploadError.value = error.message || 'Unable to load images.'
  }
  finally {
    tenantImagesLoading.value = false
  }
}

function openThumbnailPicker() {
  thumbnailPickerMode.value    = true
  imagePickerFieldTarget.value = null
  imagePickerTargetIndex.value = null
  imagePickerSelectedUrl.value = templateThumbnailUrl.value
  tenantImagesUploadError.value = ''
  imagePickerOpen.value = true
  loadTenantImages()
}

function handleImageSelect(image) {
  if (!image?.url) return

  if (thumbnailPickerMode.value) {
    templateThumbnailUrl.value = image.url
    thumbnailPickerMode.value  = false
  } else if (imagePickerFieldTarget.value) {
    const { sectionKey, fieldKey } = imagePickerFieldTarget.value
    if (!content[sectionKey]) content[sectionKey] = {}
    if (!content[sectionKey][fieldKey]) {
      content[sectionKey][fieldKey] = { ...(builderContent[sectionKey]?.[fieldKey] ?? { type: 'image', label: '', value: '' }) }
    }
    content[sectionKey][fieldKey].value = image.url
  } else {
    selectGalleryImage(image)
  }
}

function selectGalleryImage(image) {
  const galleryImage =
    content.gallery?.images?.value?.[imagePickerTargetIndex.value]

  if (!galleryImage || !image?.url) return

  galleryImage.url = image.url

  if (!galleryImage.caption && (image.caption || image.name || image.filename)) {
    galleryImage.caption = image.caption || image.name || image.filename
  }
}

function addTenantImage(image) {
  if (!image?.url) return

  const existingIndex = tenantImages.value.findIndex(existing =>
    existing.cloudflare_image_id
      ? existing.cloudflare_image_id === image.cloudflare_image_id
      : existing.url === image.url
  )

  if (existingIndex >= 0) {
    tenantImages.value.splice(existingIndex, 1, image)
    return
  }

  tenantImages.value.unshift(image)
}

function cloudflareErrorMessage(data, fallback) {
  return (
    data?.errors?.map(error => error.message).join('; ') ||
    data?.error ||
    fallback
  )
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function completeTenantImageUpload({ token, tenantId, cloudflareImageId, filename }) {
  let lastError = null

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const response = await fetch(`${API_BASE_URL}/media/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        company_id: tenantId,
        cloudflare_image_id: cloudflareImageId,
        filename,
      }),
    })
    const data = await response.json().catch(() => ({}))

    if (response.ok && data.image?.url) {
      return data.image
    }

    lastError = new Error(data.error || 'Unable to save uploaded image.')

    if (response.status !== 409 || attempt === 3) {
      throw lastError
    }

    await wait(700)
  }

  throw lastError || new Error('Unable to save uploaded image.')
}

async function uploadTenantImage(file) {
  const tenantId = effectiveTenantId.value

  if (!tenantId || !file) {
    tenantImagesUploadError.value = tenantId
      ? 'No file selected.'
      : 'Select a company above before uploading.'
    return
  }

  tenantImagesUploading.value = true
  tenantImagesUploadError.value = ''

  try {
    const token = await getAuthToken()

    if (!token) {
      throw new Error('You need to be signed in to upload images.')
    }

    const directResponse = await fetch(`${API_BASE_URL}/media/direct-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        company_id: tenantId,
        filename: file.name,
      }),
    })
    const directData = await directResponse.json().catch(() => ({}))
    const cloudflareImageId = directData.image?.cloudflare_image_id

    if (!directResponse.ok || !directData.uploadURL || !cloudflareImageId) {
      throw new Error(
        directData.error || 'Unable to prepare image upload.'
      )
    }

    const uploadForm = new FormData()
    uploadForm.append('file', file)

    const uploadResponse = await fetch(directData.uploadURL, {
      method: 'POST',
      body: uploadForm,
    })
    const uploadData = await uploadResponse.json().catch(() => ({}))

    if (!uploadResponse.ok || uploadData.success === false) {
      throw new Error(
        cloudflareErrorMessage(uploadData, 'Cloudflare upload failed.')
      )
    }

    const uploadedImage = await completeTenantImageUpload({
      token,
      tenantId,
      cloudflareImageId,
      filename: file.name,
    })

    addTenantImage(uploadedImage)
    handleImageSelect(uploadedImage)
    imagePickerOpen.value = false
  }
  catch (error) {
    console.error('uploadTenantImage error:', error)
    tenantImagesUploadError.value = error.message || 'Unable to upload image.'
  }
  finally {
    tenantImagesUploading.value = false
  }
}
async function deleteTenantImage(image) {
  const mediaId = image?.id
  if (!mediaId) return
  tenantImageDeleting.value = mediaId
  try {
    const token = await getAuthToken()
    const res = await fetch(`${API_BASE_URL}/media/${mediaId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Delete failed.')
    }
    tenantImages.value = tenantImages.value.filter(img => img.id !== mediaId)
  } catch (error) {
    console.error('deleteTenantImage error:', error)
    tenantImagesUploadError.value = error.message || 'Unable to delete image.'
  } finally {
    tenantImageDeleting.value = null
  }
}
//───────────────────────────────────────────────────────────
// End Image Modal
//───────────────────────────────────────────────────────────

//───────────────────────────────────────────────────────────
// Stock Photos (admin-curated, per industry+slot — see
// backend/routes/stockMedia.js). Reuses the same Cloudflare Images
// direct-upload + complete flow as the tenant image library above, just
// pointed at the /admin/stock-media/* endpoints and keyed by industry_key +
// slot instead of company_id.
//───────────────────────────────────────────────────────────
const stockAdminIndustries   = ref([]) // [{key, label}]
const stockAdminIndustryKey  = ref('')
const stockAdminSlots        = ref([]) // [{key, label}] — comes back from /stock-media itself
const stockAdminSlot         = ref('')
const stockAdminAltText      = ref('')
const stockAdminImages       = ref([])
const stockAdminLoading      = ref(false)
const stockAdminError        = ref('')
const stockAdminUploading    = ref(false)
const stockAdminUploadError  = ref('')
const stockAdminDeleting     = ref(null)
const stockAdminFileInput    = ref(null)

function slotLabel(slotKey) {
  return stockAdminSlots.value.find(s => s.key === slotKey)?.label || slotKey
}

async function loadStockAdminIndustries() {
  if (stockAdminIndustries.value.length) return
  try {
    const response = await fetch(`${API_BASE_URL}/ai/static-site/industries`)
    const data = await response.json().catch(() => ({}))
    if (response.ok && data.ok) stockAdminIndustries.value = data.industries || []
  } catch {
    // Non-critical — the industry select just stays empty.
  }
}

async function loadStockAdminImages() {
  if (!stockAdminIndustryKey.value) {
    stockAdminImages.value = []
    return
  }

  stockAdminLoading.value = true
  stockAdminError.value = ''

  try {
    const token = await getAuthToken()
    const response = await fetch(
      `${API_BASE_URL}/stock-media?industry_key=${encodeURIComponent(stockAdminIndustryKey.value)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Unable to load stock photos.')
    }

    stockAdminImages.value = data.images || []
    stockAdminSlots.value = data.slots || []
    if (!stockAdminSlot.value && stockAdminSlots.value.length) {
      stockAdminSlot.value = stockAdminSlots.value[0].key
    }
  } catch (e) {
    stockAdminError.value = e.message || 'Unable to load stock photos.'
  } finally {
    stockAdminLoading.value = false
  }
}

async function completeStockAdminUpload({ token, cloudflareImageId, filename }) {
  let lastError = null

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const response = await fetch(`${API_BASE_URL}/admin/stock-media/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        industry_key: stockAdminIndustryKey.value,
        slot: stockAdminSlot.value,
        cloudflare_image_id: cloudflareImageId,
        filename,
        alt_text: stockAdminAltText.value,
      }),
    })
    const data = await response.json().catch(() => ({}))

    if (response.ok && data.image?.url) {
      return data.image
    }

    lastError = new Error(data.error || 'Unable to save uploaded photo.')

    if (response.status !== 409 || attempt === 3) {
      throw lastError
    }

    await wait(700)
  }

  throw lastError || new Error('Unable to save uploaded photo.')
}

async function handleStockAdminFileInput(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return

  if (!stockAdminIndustryKey.value || !stockAdminSlot.value) {
    stockAdminUploadError.value = 'Select an industry and slot first.'
    return
  }

  stockAdminUploading.value = true
  stockAdminUploadError.value = ''

  try {
    const token = await getAuthToken()
    if (!token) throw new Error('You need to be signed in to upload images.')

    const directResponse = await fetch(`${API_BASE_URL}/admin/stock-media/direct-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        industry_key: stockAdminIndustryKey.value,
        slot: stockAdminSlot.value,
        filename: file.name,
      }),
    })
    const directData = await directResponse.json().catch(() => ({}))
    const cloudflareImageId = directData.image?.cloudflare_image_id

    if (!directResponse.ok || !directData.uploadURL || !cloudflareImageId) {
      throw new Error(directData.error || 'Unable to prepare image upload.')
    }

    const uploadForm = new FormData()
    uploadForm.append('file', file)

    const uploadResponse = await fetch(directData.uploadURL, {
      method: 'POST',
      body: uploadForm,
    })
    const uploadData = await uploadResponse.json().catch(() => ({}))

    if (!uploadResponse.ok || uploadData.success === false) {
      throw new Error(cloudflareErrorMessage(uploadData, 'Cloudflare upload failed.'))
    }

    const uploadedImage = await completeStockAdminUpload({
      token,
      cloudflareImageId,
      filename: file.name,
    })

    stockAdminImages.value.unshift(uploadedImage)
    stockAdminAltText.value = ''
  } catch (e) {
    console.error('handleStockAdminFileInput error:', e)
    stockAdminUploadError.value = e.message || 'Unable to upload photo.'
  } finally {
    stockAdminUploading.value = false
  }
}

async function deleteStockAdminImage(image) {
  stockAdminDeleting.value = image.id
  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/admin/stock-media/${image.id}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Unable to delete photo.')
    }

    stockAdminImages.value = stockAdminImages.value.filter(img => img.id !== image.id)
  } catch (e) {
    stockAdminError.value = e.message || 'Unable to delete photo.'
  } finally {
    stockAdminDeleting.value = null
  }
}
//───────────────────────────────────────────────────────────
// End Stock Photos
//───────────────────────────────────────────────────────────

//───────────────────────────────────────────────────────────
// Domain Reservations (beta reserve-and-fulfill flow — see
// backend/routes/domainReservations.js). Staff completes the real
// Cloudflare registration/connect by hand, then uses this tab to record
// that as done — it does not call Cloudflare itself.
//───────────────────────────────────────────────────────────
const domainReservations        = ref([])
const domainReservationsLoading = ref(false)
const domainReservationsError   = ref('')
const domainReservationActing   = ref(null)

async function loadDomainReservations() {
  domainReservationsLoading.value = true
  domainReservationsError.value = ''
  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/admin/domain-reservations`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to load reservations.')
    domainReservations.value = data.reservations || []
  } catch (e) {
    domainReservationsError.value = e.message || 'Unable to load reservations.'
  } finally {
    domainReservationsLoading.value = false
  }
}

async function fulfillDomainReservation(reservation) {
  if (!window.confirm(`Confirm ${reservation.domain} has actually been registered/connected on Cloudflare?`)) return
  domainReservationActing.value = reservation.id
  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/admin/domain-reservations/${reservation.id}/fulfill`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to mark as fulfilled.')
    domainReservations.value = domainReservations.value.filter(r => r.id !== reservation.id)
  } catch (e) {
    domainReservationsError.value = e.message || 'Unable to mark as fulfilled.'
  } finally {
    domainReservationActing.value = null
  }
}

async function cancelDomainReservation(reservation) {
  if (!window.confirm(`Cancel the reservation for ${reservation.domain}?`)) return
  domainReservationActing.value = reservation.id
  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/admin/domain-reservations/${reservation.id}/cancel`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to cancel reservation.')
    domainReservations.value = domainReservations.value.filter(r => r.id !== reservation.id)
  } catch (e) {
    domainReservationsError.value = e.message || 'Unable to cancel reservation.'
  } finally {
    domainReservationActing.value = null
  }
}
//───────────────────────────────────────────────────────────
// End Domain Reservations
//───────────────────────────────────────────────────────────
</script>








<template>
  <main class="admin-page">
    <section class="admin-shell">
      <section class="admin-panel">

        <!-- Tab bar -->
        <div class="tab-bar">
          <div class="tabs">
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/users' }"
              type="button"
              @click="selectedAdmin = '/admin/users'"
            >
              <v-icon size="15">mdi-account-group-outline</v-icon>
              Users
              <span v-if="users.length" class="tab-count">{{ users.length }}</span>
            </button>
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/companies' }"
              type="button"
              @click="selectedAdmin = '/admin/companies'"
            >
              <v-icon size="15">mdi-domain</v-icon>
              Companies
              <span v-if="companies.length" class="tab-count">{{ companies.length }}</span>
            </button>
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/admins' }"
              type="button"
              @click="selectedAdmin = '/admin/admins'"
            >
              <v-icon size="15">mdi-shield-account-outline</v-icon>
              Admins
              <span v-if="admins.length" class="tab-count">{{ admins.length }}</span>
            </button>
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/logs' }"
              type="button"
              @click="selectedAdmin = '/admin/logs'"
            >
              <v-icon size="15">mdi-history</v-icon>
              Logs
            </button>
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/templates' }"
              type="button"
              @click="selectedAdmin = '/admin/templates'; loadAllDbTemplates()"
            >
              <v-icon size="15">mdi-palette-outline</v-icon>
              Templates
            </button>
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/database' }"
              type="button"
              @click="selectedAdmin = '/admin/database'; loadDbTables()"
            >
              <v-icon size="15">mdi-database-outline</v-icon>
              Database
            </button>
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/print-templates' }"
              type="button"
              @click="selectedAdmin = '/admin/print-templates'; loadPrintTemplates()"
            >
              <v-icon size="15">mdi-card-account-mail-outline</v-icon>
              Print Templates
            </button>
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/stock-media' }"
              type="button"
              @click="selectedAdmin = '/admin/stock-media'; loadStockAdminIndustries(); loadStockAdminImages()"
            >
              <v-icon size="15">mdi-image-multiple-outline</v-icon>
              Stock Photos
            </button>
            <button
              class="tab"
              :class="{ 'tab--active': selectedAdmin === '/admin/domain-reservations' }"
              type="button"
              @click="selectedAdmin = '/admin/domain-reservations'; loadDomainReservations()"
            >
              <v-icon size="15">mdi-earth-arrow-right</v-icon>
              Domain Reservations
              <span v-if="domainReservations.length" class="tab-count">{{ domainReservations.length }}</span>
            </button>
          </div>
          <button class="tab-refresh" type="button" :disabled="loading" @click="refreshActive">
            <v-progress-circular v-if="loading" indeterminate size="15" width="2" color="var(--text-secondary)" />
            <v-icon v-else size="15" color="var(--text-secondary)">mdi-refresh</v-icon>
          </button>
        </div>

        <div v-if="loadError" class="admin-error">{{ loadError }}</div>

        <!-- ── Contractors: master-detail ── -->
        <div v-if="selectedAdmin === '/admin/users'" class="master-detail">

          <!-- LEFT: list panel -->
          <aside class="list-panel">
            <div class="search-wrap">
              <v-icon size="15" class="search-icon">mdi-magnify</v-icon>
              <input
                v-model="searchQuery"
                class="search-input"
                placeholder="Search by name, email…"
                autocomplete="off"
              />
              <button v-if="searchQuery" class="search-clear" type="button" @click="searchQuery = ''">
                <v-icon size="13">mdi-close</v-icon>
              </button>
            </div>

            <div class="list-toggle">
              <button
                class="toggle-btn"
                :class="{ 'toggle-btn--active': !showAll }"
                type="button"
                @click="showAll = false"
              >Owners</button>
              <button
                class="toggle-btn"
                :class="{ 'toggle-btn--active': showAll }"
                type="button"
                @click="showAll = true"
              >All users</button>
            </div>

            <div class="list-meta">
              <span v-if="isSearching" class="list-meta-note list-meta-note--search">
                All users · {{ listUsers.length }} result{{ listUsers.length !== 1 ? 's' : '' }}
              </span>
              <span v-else class="list-meta-note">
                {{ listUsers.length }} {{ showAll ? 'user' : 'owner' }}{{ listUsers.length !== 1 ? 's' : '' }}
              </span>
            </div>

            <div v-if="!loading && listUsers.length === 0" class="list-empty">
              <v-icon size="26" color="var(--text-secondary)">mdi-account-search-outline</v-icon>
              <span>No results</span>
            </div>

            <button
              v-for="u in listUsers"
              :key="u.firebase_uid"
              class="list-item"
              :class="{ 'list-item--active': selectedUid === u.firebase_uid }"
              type="button"
              @click="selectedUid = u.firebase_uid"
            >
              <span class="list-avatar" :class="'avatar-' + (u.role || 'staff')">
                {{ initials(u) }}
              </span>
              <div class="list-body">
                <span class="list-name">{{ displayName(u) }}</span>
                <span class="list-sub">{{ companyFor(u)?.company_name || u.email || '—' }}</span>
              </div>
              <span v-if="u.role && u.role !== 'owner'" class="role-chip" :class="'role-' + u.role">
                {{ u.role }}
              </span>
            </button>
          </aside>

          <!-- RIGHT: detail panel -->
          <section class="detail-panel">

            <!-- Empty state -->
            <div v-if="!selectedUser" class="detail-empty">
              <v-icon size="38" color="var(--border-subtle)">mdi-account-details-outline</v-icon>
              <p>Select a contractor to view their profile</p>
            </div>

            <template v-else>

              <!-- User header -->
              <div class="detail-section">
                <div class="user-header">
                  <div class="user-header-left">
                    <div class="detail-avatar" :class="'avatar-' + (selectedUser.role || 'staff')">
                      {{ initials(selectedUser) }}
                    </div>
                    <div>
                      <h2 class="detail-name">{{ displayName(selectedUser) }}</h2>
                      <span class="detail-email">{{ selectedUser.email || '—' }}</span>
                    </div>
                  </div>
                  <div class="user-header-right">
                    <span class="role-chip" :class="'role-' + selectedUser.role">{{ selectedUser.role }}</span>
                    <button class="act-btn" type="button" @click="openEditUser(selectedUser)">
                      <v-icon size="13">mdi-pencil-outline</v-icon> Edit
                    </button>
                    <button class="act-btn act-btn--danger" type="button" @click="openDeleteUser(selectedUser)">
                      <v-icon size="13">mdi-trash-can-outline</v-icon> Delete
                    </button>
                  </div>
                </div>
                <div class="meta-row">
                  <span class="meta-item">
                    <span class="meta-label">UID</span>
                    <code class="meta-code">{{ truncate(selectedUser.firebase_uid, 30) }}</code>
                  </span>
                  <span class="meta-item">
                    <span class="meta-label">Member since</span>
                    {{ formatDate(selectedUser.created_at) }}
                  </span>
                </div>
              </div>

              <div class="detail-divider" />

              <!-- Company -->
              <div class="detail-section">
                <div class="section-head">
                  <div class="section-label-group">
                    <v-icon size="14" color="#1f5c4d">mdi-domain</v-icon>
                    <span class="section-heading">Company</span>
                  </div>
                  <div v-if="selectedCompany" class="section-acts">
                    <button class="act-btn" type="button" @click="openEditCompany(selectedCompany)">
                      <v-icon size="13">mdi-pencil-outline</v-icon> Edit
                    </button>
                    <button class="act-btn act-btn--cms" type="button" @click="manageCMS(selectedCompany.company_id)">
                      <v-icon size="13">mdi-view-dashboard-edit-outline</v-icon> Open CMS
                    </button>
                  </div>
                </div>
                <div v-if="selectedCompany" class="info-grid">
                  <div class="info-cell">
                    <span class="meta-label">Name</span>
                    <span>{{ selectedCompany.company_name || '—' }}</span>
                  </div>
                  <div class="info-cell">
                    <span class="meta-label">Industry</span>
                    <span>{{ [selectedCompany.industry, selectedCompany.sub_industry].filter(Boolean).join(' › ') || '—' }}</span>
                  </div>
                  <div class="info-cell">
                    <span class="meta-label">Email</span>
                    <span>{{ selectedCompany.company_email || '—' }}</span>
                  </div>
                  <div class="info-cell">
                    <span class="meta-label">Phone</span>
                    <span>{{ selectedCompany.company_phone || '—' }}</span>
                  </div>
                  <div class="info-cell">
                    <span class="meta-label">Main Contact</span>
                    <span>{{ selectedCompany.main_contact_name || '—' }}</span>
                  </div>
                  <div class="info-cell">
                    <span class="meta-label">Company ID</span>
                    <code class="meta-code">{{ truncate(selectedCompany.company_id, 22) }}</code>
                  </div>
                </div>
                <p v-else class="detail-none">No company record found.</p>
              </div>

              <div class="detail-divider" />

              <!-- Website -->
              <div class="detail-section">
                <div class="section-head">
                  <div class="section-label-group">
                    <v-icon size="14" color="#1f5c4d">mdi-web</v-icon>
                    <span class="section-heading">Website</span>
                  </div>
                  <div v-if="selectedWebsite" class="section-acts">
                    <button class="act-btn act-btn--cms" type="button" @click="manageCMS(selectedWebsite.company_id)">
                      <v-icon size="13">mdi-view-dashboard-edit-outline</v-icon> Open CMS
                    </button>
                  </div>
                </div>
                <div v-if="selectedWebsite" class="info-grid">
                  <div class="info-cell">
                    <span class="meta-label">Template</span>
                    <span>{{ selectedWebsite.template_id || '—' }}</span>
                  </div>
                  <div class="info-cell">
                    <span class="meta-label">Quotastic URL</span>
                    <span>{{ selectedWebsite.quotastic_domain || '—' }}</span>
                  </div>
                  <div class="info-cell">
                    <span class="meta-label">Custom Domain</span>
                    <span>{{ selectedWebsite.domain || 'Not set' }}</span>
                  </div>
                  <div class="info-cell">
                    <span class="meta-label">Last Updated</span>
                    <span>{{ formatDate(selectedWebsite.updated_at) }}</span>
                  </div>
                </div>
                <p v-else class="detail-none">No website record found.</p>
              </div>

              <div class="detail-divider" />

              <!-- Team -->
              <div class="detail-section">
                <div class="section-head">
                  <div class="section-label-group">
                    <v-icon size="14" color="#1f5c4d">mdi-account-multiple-outline</v-icon>
                    <span class="section-heading">Team</span>
                    <span class="count-badge">{{ selectedTeam.length }}</span>
                  </div>
                </div>
                <div v-if="selectedTeam.length" class="team-list">
                  <div v-for="m in selectedTeam" :key="m.firebase_uid" class="team-row">
                    <span class="team-avatar" :class="'avatar-' + (m.role || 'staff')">{{ initials(m) }}</span>
                    <div class="team-info">
                      <span class="team-name">{{ displayName(m) }}</span>
                      <span class="team-email">{{ m.email }}</span>
                    </div>
                    <span class="role-chip role-chip--sm" :class="'role-' + m.role">{{ m.role }}</span>
                    <div class="team-acts">
                      <button class="row-btn" type="button" title="Edit" @click="openEditUser(m)">
                        <v-icon size="13">mdi-pencil-outline</v-icon>
                      </button>
                      <button class="row-btn row-btn--danger" type="button" title="Delete" @click="openDeleteUser(m)">
                        <v-icon size="13">mdi-trash-can-outline</v-icon>
                      </button>
                    </div>
                  </div>
                </div>
                <p v-else class="detail-none">No other team members.</p>
              </div>

              <div class="detail-divider" />

              <!-- Recent Activity -->
              <div class="detail-section">
                <div class="section-head">
                  <div class="section-label-group">
                    <v-icon size="14" color="#1f5c4d">mdi-history</v-icon>
                    <span class="section-heading">Recent Activity</span>
                  </div>
                  <button class="act-btn" type="button" @click="selectedAdmin = '/admin/logs'; logFilterUid = selectedUid">
                    <v-icon size="13">mdi-arrow-top-right</v-icon> View all
                  </button>
                </div>
                <div v-if="activityLoading" class="activity-loading">
                  <v-progress-circular indeterminate size="18" width="2" color="var(--text-secondary)" />
                </div>
                <p v-else-if="activityError" class="detail-none" style="color:#9f2a1d">{{ activityError }}</p>
                <div v-else-if="activityLogs.length" class="activity-list">
                  <div v-for="log in activityLogs" :key="log.id" class="activity-row">
                    <span class="activity-chip" :style="{ background: actionColor(log.action) + '18', color: actionColor(log.action) }">
                      {{ log.action }}
                    </span>
                    <span class="activity-label">{{ log.label || log.action }}</span>
                    <span class="activity-time">{{ formatRelative(log.created_at) }}</span>
                  </div>
                </div>
                <p v-else class="detail-none">No activity recorded yet.</p>
              </div>

            </template>
          </section>
        </div>

        <!-- ── Companies: searchable table ── -->
        <template v-else-if="selectedAdmin === '/admin/companies'">
          <div class="co-toolbar">
            <div class="co-search-wrap">
              <v-icon size="15" class="search-icon">mdi-magnify</v-icon>
              <input
                v-model="companySearch"
                class="search-input"
                placeholder="Search by name, industry, email, owner…"
                autocomplete="off"
              />
              <button v-if="companySearch" class="search-clear" type="button" @click="companySearch = ''">
                <v-icon size="13">mdi-close</v-icon>
              </button>
            </div>
            <span class="co-count">{{ listCompanies.length }} result{{ listCompanies.length !== 1 ? 's' : '' }}</span>
          </div>

          <div class="admin-table-wrap">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Industry</th>
                  <th>Owner</th>
                  <th>Domain</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody v-if="listCompanies.length">
                <tr v-for="c in listCompanies" :key="c.company_id">
                  <td>
                    <div class="co-name">{{ c.company_name || '—' }}</div>
                    <div class="co-sub">{{ c.company_email || '' }}</div>
                  </td>
                  <td>
                    <span v-if="c.industry">{{ c.industry }}</span>
                    <span v-if="c.sub_industry" class="co-sub"> · {{ c.sub_industry }}</span>
                    <span v-if="!c.industry" class="co-sub">—</span>
                  </td>
                  <td>
                    <template v-if="ownerFor(c)">
                      <div class="co-name">{{ displayName(ownerFor(c)) }}</div>
                      <div class="co-sub">{{ ownerFor(c).email }}</div>
                    </template>
                    <span v-else class="co-sub">—</span>
                  </td>
                  <td>
                    <span v-if="websiteFor(c)?.domain" class="co-domain">{{ websiteFor(c).domain }}</span>
                    <span v-else-if="websiteFor(c)?.quotastic_domain" class="co-sub">{{ websiteFor(c).quotastic_domain }}</span>
                    <span v-else class="co-sub">—</span>
                  </td>
                  <td class="col-actions">
                    <div class="row-actions">
                      <button class="row-btn" type="button" title="Edit" @click="openEditCompany(c)">
                        <v-icon size="13">mdi-pencil-outline</v-icon>
                      </button>
                      <button class="row-btn row-btn--cms" type="button" title="Open CMS" @click="manageCMS(c.company_id)">
                        <v-icon size="13">mdi-view-dashboard-edit-outline</v-icon>
                        CMS
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr><td class="empty-cell" colspan="5">No companies found.</td></tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- ── Logs: global activity table ── -->
        <template v-else-if="selectedAdmin === '/admin/logs'">
          <div class="co-toolbar" style="gap:8px; flex-wrap:wrap;">
            <div class="co-search-wrap" style="min-width:180px; flex:2;">
              <v-icon size="15" class="search-icon">mdi-account-outline</v-icon>
              <input
                v-model="logFilterUid"
                class="search-input"
                placeholder="Filter by Firebase UID…"
                autocomplete="off"
              />
              <button v-if="logFilterUid" class="search-clear" type="button" @click="logFilterUid = ''">
                <v-icon size="13">mdi-close</v-icon>
              </button>
            </div>
            <select v-model="logFilterAction" class="log-select">
              <option value="">All actions</option>
              <option value="login">Login</option>
              <option value="quote.">Quotes</option>
              <option value="website.">Website</option>
              <option value="domain.">Domain</option>
              <option value="dns.">DNS</option>
              <option value="company.">Company</option>
            </select>
            <input v-model="logFilterFrom" type="date" class="log-date" title="From date" />
            <input v-model="logFilterTo"   type="date" class="log-date" title="To date" />
            <button class="admin-action admin-action--primary" type="button" :disabled="globalLogsLoading" @click="loadGlobalLogs">
              <v-progress-circular v-if="globalLogsLoading" indeterminate size="14" width="2" />
              <v-icon v-else size="15">mdi-magnify</v-icon>
              Search
            </button>
          </div>
          <div v-if="globalLogsError" class="admin-error">{{ globalLogsError }}</div>
          <div class="admin-table-wrap">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Label</th>
                  <th>Company</th>
                </tr>
              </thead>
              <tbody v-if="globalLogs.length">
                <tr v-for="log in globalLogs" :key="log.id">
                  <td style="white-space:nowrap; color:var(--text-secondary); font-size:0.8rem;">
                    {{ formatRelative(log.created_at) }}
                    <div style="font-size:0.72rem; color:var(--text-secondary);">{{ new Date(log.created_at).toLocaleString('en-CA', { hour12: false }) }}</div>
                  </td>
                  <td>
                    <template v-if="userFor(log.firebase_uid)">
                      <div class="co-name">{{ displayName(userFor(log.firebase_uid)) }}</div>
                      <div class="co-sub">{{ userFor(log.firebase_uid).email }}</div>
                    </template>
                    <code v-else class="meta-code" style="font-size:0.72rem;">{{ truncate(log.firebase_uid, 18) }}</code>
                  </td>
                  <td>
                    <span class="activity-chip" :style="{ background: actionColor(log.action) + '18', color: actionColor(log.action) }">
                      {{ log.action }}
                    </span>
                  </td>
                  <td style="max-width:220px; white-space:normal; font-size:0.84rem;">{{ log.label || '—' }}</td>
                  <td>
                    <span v-if="log.company_id" style="font-size:0.8rem; color:var(--text-secondary);">
                      {{ companies.find(c => c.company_id === log.company_id)?.company_name || truncate(log.company_id, 14) }}
                    </span>
                    <span v-else class="co-sub">—</span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else-if="!globalLogsLoading">
                <tr><td class="empty-cell" colspan="5">No logs found. Click Search to load.</td></tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- ── Template Builder ── -->
        <template v-else-if="selectedAdmin === '/admin/templates'">
          <div class="admin-toolbar">
            <div>
              <strong>Template Builder</strong>
              <span>Paste an AI-generated template to validate and preview</span>
            </div>
          </div>

          <div class="tpl-thumbnail-row">
            <div class="tpl-meta-fields" style="align-items:end">
              <div class="tpl-meta-field" style="grid-column: span 2">
                <label class="tpl-label">Thumbnail URL</label>
                <div class="tpl-thumbnail-input-row">
                  <input
                    v-model="templateThumbnailUrl"
                    class="tpl-meta-input"
                    type="text"
                    placeholder="https://… or choose from library"
                  />
                  <button
                    class="admin-action"
                    type="button"
                    @click="openThumbnailPicker"
                  >
                    <v-icon size="15">mdi-image-outline</v-icon> Choose
                  </button>
                </div>
              </div>
              <div class="tpl-meta-field" style="align-items:center; justify-content:center">
                <img
                  v-if="templateThumbnailUrl"
                  :src="templateThumbnailUrl"
                  class="tpl-thumbnail-preview"
                  alt="Thumbnail preview"
                />
                <span v-else class="tpl-thumbnail-empty">No thumbnail</span>
              </div>
            </div>
          </div>

          <div class="tpl-builder">

            <div class="tpl-editor-col">
              <div class="tpl-load-row">
                <select
                  class="tpl-load-select"
                  :value="selectedLoadId"
                  :disabled="allDbTemplatesLoading"
                  @change="selectedLoadId = $event.target.value; loadExistingTemplate($event.target.value)"
                >
                  <option value="">
                    {{ allDbTemplatesLoading ? 'Loading…' : allDbTemplatesError ? `Error: ${allDbTemplatesError}` : '— Load existing template to edit —' }}
                  </option>
                  <option
                    v-for="t in allDbTemplates"
                    :key="t.id"
                    :value="t.id"
                  >{{ t.name }} ({{ t.status }})</option>
                </select>
                <button
                  v-if="allDbTemplatesError"
                  class="admin-action"
                  type="button"
                  @click="loadAllDbTemplates"
                >
                  <v-icon size="14">mdi-refresh</v-icon> Retry
                </button>
                <span class="tpl-load-divider">or paste new JSON below</span>
              </div>

              <div class="tpl-meta-fields">
                <div class="tpl-meta-field">
                  <label class="tpl-label">
                    ID <span class="tpl-meta-required">*</span>
                    <span v-if="selectedLoadId" class="tpl-label-sub">(locked while editing)</span>
                  </label>
                  <input
                    v-model="templateIdInput"
                    class="tpl-meta-input"
                    type="text"
                    placeholder="e.g. garden04"
                    spellcheck="false"
                    :disabled="!!selectedLoadId"
                  />
                </div>
                <div class="tpl-meta-field">
                  <label class="tpl-label">Name <span class="tpl-meta-required">*</span></label>
                  <input
                    v-model="templateNameInput"
                    class="tpl-meta-input"
                    type="text"
                    placeholder="e.g. Garden 04"
                  />
                </div>
                <div class="tpl-meta-field">
                  <label class="tpl-label">Category</label>
                  <input
                    v-model="templateCategoryInput"
                    class="tpl-meta-input"
                    type="text"
                    placeholder="e.g. landscaping"
                  />
                </div>
              </div>

              <label class="tpl-label">Template JSON <span class="tpl-label-sub">(template, css, content)</span></label>
              <textarea
                class="tpl-textarea"
                placeholder="Paste template object here..."
                v-model="templatePasteInput"
                spellcheck="false"
              />
              <div class="tpl-actions">
                <button class="admin-action admin-action--primary" type="button" @click="validateTemplate">
                  <v-icon size="15">mdi-check-circle-outline</v-icon> Validate &amp; Preview
                </button>
                <button class="admin-action" type="button" @click="clearTemplate">
                  <v-icon size="15">mdi-close</v-icon> Clear
                </button>
              </div>
              <div v-if="templateParseError" class="tpl-error">
                <v-icon size="15">mdi-alert-circle-outline</v-icon>
                {{ templateParseError }}
              </div>
              <template v-else-if="templateParsed">
                <div class="tpl-success">
                  <v-icon size="15">mdi-check-circle-outline</v-icon>
                  Valid — id: <strong>{{ templateOriginalId }}</strong> &nbsp;·&nbsp; name: <strong>{{ templateParsed.name }}</strong>
                </div>
                <div class="tpl-actions" style="margin-top:10px">
                  <button
                    class="admin-action admin-action--primary"
                    type="button"
                    :disabled="templateSaving"
                    @click="saveTemplate('active')"
                  >
                    <v-icon size="15">mdi-cloud-upload-outline</v-icon>
                    {{ templateSaving ? 'Saving…' : 'Publish' }}
                  </button>
                  <button
                    class="admin-action"
                    type="button"
                    :disabled="templateSaving"
                    @click="saveTemplate('draft')"
                  >
                    <v-icon size="15">mdi-content-save-outline</v-icon>
                    Save as Draft
                  </button>
                </div>
                <div v-if="templateSaveError" class="tpl-error" style="margin-top:8px">
                  <v-icon size="15">mdi-alert-circle-outline</v-icon> {{ templateSaveError }}
                </div>
                <div v-if="templateSaveSuccess" class="tpl-success" style="margin-top:8px">
                  <v-icon size="15">mdi-check-circle-outline</v-icon> {{ templateSaveSuccess }}
                </div>
              </template>
            </div>

            <div class="tpl-preview-col">
              <label class="tpl-label">Preview</label>
              <iframe
                v-if="showTemplatePreview"
                ref="templatePreviewIframe"
                :src="`/site-preview/${PREVIEW_ID}`"
                class="tpl-preview-iframe"
                @load="onPreviewLoad"
                style="zoom:60%"
              />
              <div v-else class="tpl-preview-placeholder">
                <v-icon size="40" color="var(--border-subtle)">mdi-monitor-screenshot</v-icon>
                <span>Preview will appear here</span>
              </div>
            </div>

          </div>
        </template>

        <!-- ── Database explorer ── -->
        <template v-else-if="selectedAdmin === '/admin/database'">
          <div class="master-detail">

            <!-- LEFT: table list -->
            <aside class="list-panel">
              <div class="list-meta">
                <span class="list-meta-note">
                  {{ dbTables.length }} table{{ dbTables.length !== 1 ? 's' : '' }}
                </span>
              </div>

              <div v-if="dbTablesLoading" class="activity-loading">
                <v-progress-circular indeterminate size="22" width="2" color="#1f5c4d" />
              </div>
              <div v-else-if="dbTablesError" class="admin-error">{{ dbTablesError }}</div>
              <div v-else-if="!dbTables.length" class="list-empty">
                <v-icon size="26" color="var(--text-secondary)">mdi-database-off-outline</v-icon>
                <span>No tables found</span>
              </div>

              <button
                v-for="t in dbTables"
                :key="t.name"
                class="list-item"
                :class="{ 'list-item--active': selectedDbTable === t.name }"
                type="button"
                @click="selectDbTable(t.name)"
              >
                <div class="list-body">
                  <span class="list-name">{{ t.name }}</span>
                  <span class="list-sub">{{ t.approximate ? '~' : '' }}{{ t.rowCount }} row{{ t.rowCount !== 1 ? 's' : '' }}</span>
                </div>
              </button>
            </aside>

            <!-- RIGHT: table data -->
            <section class="detail-panel">
              <div v-if="!selectedDbTable" class="detail-empty">
                <v-icon size="38" color="var(--border-subtle)">mdi-table-eye</v-icon>
                <p>Select a table to view its data</p>
              </div>

              <template v-else>
                <div class="admin-toolbar">
                  <div>
                    <strong>{{ selectedDbTable }}</strong>
                    <span>{{ dbTotal }} row{{ dbTotal !== 1 ? 's' : '' }} · read-only</span>
                  </div>
                  <select class="log-select" :value="dbPageSize" @change="changeDbPageSize(Number($event.target.value))">
                    <option :value="25">25 / page</option>
                    <option :value="50">50 / page</option>
                    <option :value="100">100 / page</option>
                  </select>
                </div>

                <div v-if="dbError" class="admin-error">{{ dbError }}</div>

                <div v-if="dbRowsLoading" class="activity-loading">
                  <v-progress-circular indeterminate size="22" width="2" color="#1f5c4d" />
                </div>

                <template v-else>
                  <div class="admin-table-wrap">
                    <table class="admin-table">
                      <thead>
                        <tr>
                          <th
                            v-for="col in dbColumns"
                            :key="col.name"
                            class="db-sortable-th"
                            @click="sortDbColumn(col.name)"
                          >
                            {{ col.name }}
                            <v-icon v-if="dbSort === col.name" size="12">
                              {{ dbDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                            </v-icon>
                          </th>
                        </tr>
                      </thead>
                      <tbody v-if="dbRows.length">
                        <tr v-for="(row, i) in dbRows" :key="i">
                          <td v-for="col in dbColumns" :key="col.name">
                            <template v-if="looksLikeJson(row[col.name])">
                              <button
                                class="db-json-toggle"
                                type="button"
                                @click="expandedDbCell = expandedDbCell === dbCellKey(i, col.name) ? null : dbCellKey(i, col.name)"
                              >
                                {{ expandedDbCell === dbCellKey(i, col.name) ? 'Hide' : 'View' }} JSON
                              </button>
                              <pre v-if="expandedDbCell === dbCellKey(i, col.name)" class="db-json-pre">{{ prettyJson(row[col.name]) }}</pre>
                            </template>
                            <span v-else :title="formatDbCell(row[col.name], col.type)">
                              {{ truncate(formatDbCell(row[col.name], col.type), 60) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr><td class="empty-cell" :colspan="dbColumns.length || 1">No rows.</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div class="db-pager">
                    <button
                      class="admin-action"
                      type="button"
                      :disabled="dbPage <= 1"
                      @click="loadDbTableRows(selectedDbTable, dbPage - 1)"
                    >
                      <v-icon size="15">mdi-chevron-left</v-icon> Prev
                    </button>
                    <span class="db-pager-label">Page {{ dbPage }} of {{ dbTotalPages }}</span>
                    <button
                      class="admin-action"
                      type="button"
                      :disabled="dbPage >= dbTotalPages"
                      @click="loadDbTableRows(selectedDbTable, dbPage + 1)"
                    >
                      Next <v-icon size="15">mdi-chevron-right</v-icon>
                    </button>
                  </div>
                </template>
              </template>
            </section>
          </div>
        </template>

        <!-- ── Print Templates: master-detail with live preview ── -->
        <template v-else-if="selectedAdmin === '/admin/print-templates'">
          <div class="master-detail">
            <aside class="list-panel">
              <button class="admin-action admin-action--primary pt-new-btn" type="button" @click="newPrintTemplate">
                <v-icon size="16">mdi-plus</v-icon> New Print Template
              </button>

              <div v-if="!printTemplatesLoading && printTemplatesAdmin.length === 0" class="list-empty">
                <v-icon size="26" color="var(--text-secondary)">mdi-card-account-mail-outline</v-icon>
                <span>No print templates yet</span>
              </div>

              <button
                v-for="t in printTemplatesAdmin"
                :key="t.id"
                class="list-item"
                :class="{ 'list-item--active': selectedPrintTemplateId === t.id }"
                type="button"
                @click="selectPrintTemplate(t.id)"
              >
                <div class="list-body">
                  <span class="list-name">{{ t.name }}</span>
                  <span class="list-sub">{{ t.category || '—' }} · {{ t.status }}</span>
                </div>
              </button>
            </aside>

            <section class="detail-panel">
              <div v-if="!printTemplateForm" class="detail-empty">
                <v-icon size="38" color="var(--border-subtle)">mdi-card-account-mail-outline</v-icon>
                <p>Select a print template to edit, or create a new one</p>
              </div>

              <template v-else>
                <div class="pt-layout">
                  <div class="pt-form">
                    <div class="pt-form-row">
                      <label class="field">
                        <span>ID (slug)</span>
                        <input class="form-field" type="text" v-model="printTemplateForm.id" :disabled="printTemplateIsExisting" />
                      </label>
                      <label class="field">
                        <span>Name</span>
                        <input class="form-field" type="text" v-model="printTemplateForm.name" />
                      </label>
                      <label class="field">
                        <span>Category</span>
                        <input class="form-field" type="text" v-model="printTemplateForm.category" placeholder="business-card" />
                      </label>
                      <label class="field">
                        <span>Status</span>
                        <select class="form-field" v-model="printTemplateForm.status">
                          <option value="draft">Draft</option>
                          <option value="active">Active</option>
                          <option value="archived">Archived</option>
                        </select>
                      </label>
                      <label class="field">
                        <span>Width (in)</span>
                        <input class="form-field" type="number" step="0.001" v-model="printTemplateForm.width_in" />
                      </label>
                      <label class="field">
                        <span>Height (in)</span>
                        <input class="form-field" type="number" step="0.001" v-model="printTemplateForm.height_in" />
                      </label>
                      <label class="field">
                        <span>Bleed (in)</span>
                        <input class="form-field" type="number" step="0.001" v-model="printTemplateForm.bleed_in" />
                      </label>
                      <label class="field">
                        <span>Sort Order</span>
                        <input class="form-field" type="number" v-model="printTemplateForm.sort_order" />
                      </label>
                    </div>

                    <label class="field">
                      <span>Template Markup</span>
                      <textarea class="form-field pt-textarea" v-model="printTemplateForm.template" rows="10"></textarea>
                    </label>

                    <label class="field">
                      <span>CSS</span>
                      <textarea class="form-field pt-textarea" v-model="printTemplateForm.css" rows="8"></textarea>
                    </label>

                    <label class="field">
                      <span>Content (JSON — field descriptors)</span>
                      <textarea class="form-field pt-textarea" v-model="printTemplateForm.contentJson" rows="10"></textarea>
                    </label>

                    <div v-if="printTemplateError" class="admin-error">{{ printTemplateError }}</div>

                    <div class="pt-form-actions">
                      <button class="admin-action admin-action--primary" type="button" @click="savePrintTemplate" :disabled="printTemplateSaving">
                        <v-progress-circular v-if="printTemplateSaving" indeterminate size="15" width="2" />
                        <template v-else>
                          <v-icon size="15">mdi-content-save-outline</v-icon>
                          Save
                        </template>
                      </button>
                      <button
                        v-if="printTemplateIsExisting"
                        class="admin-action pt-delete-btn"
                        type="button"
                        @click="deletePrintTemplate"
                      >
                        <v-icon size="15">mdi-delete-outline</v-icon>
                        Delete
                      </button>
                    </div>
                  </div>

                  <div class="pt-preview">
                    <p class="pt-preview-label">Preview ({{ printTemplateForm.width_in }}in × {{ printTemplateForm.height_in }}in)</p>
                    <div class="pt-preview-canvas" :style="printPreviewCanvasStyle">
                      <component :is="printPreviewComponent" v-if="printPreviewComponent" :preview-content="printPreviewContent" />
                      <component :is="'style'" :key="printPreviewCssKey">{{ printTemplateForm.css }}</component>
                    </div>
                  </div>
                </div>
              </template>
            </section>
          </div>
        </template>

        <!-- ── Stock photos: curated per industry+slot, used by Builder3 instead of a random picsum placeholder (backend/routes/stockMedia.js) ── -->
        <template v-else-if="selectedAdmin === '/admin/stock-media'">
          <div class="admin-toolbar">
            <div>
              <strong>Stock Photos</strong>
              <span>Curated per industry + slot. Generation falls back to a picsum placeholder wherever a slot has no photo here.</span>
            </div>
          </div>

          <div class="stock-media-controls">
            <label class="field">
              <span>Industry</span>
              <select class="form-field" v-model="stockAdminIndustryKey" @change="loadStockAdminImages">
                <option value="" disabled>Select an industry…</option>
                <option v-for="ind in stockAdminIndustries" :key="ind.key" :value="ind.key">{{ ind.label }}</option>
              </select>
            </label>

            <template v-if="stockAdminIndustryKey">
              <label class="field">
                <span>Slot for new upload</span>
                <select class="form-field" v-model="stockAdminSlot">
                  <option v-for="slot in stockAdminSlots" :key="slot.key" :value="slot.key">{{ slot.label }}</option>
                </select>
              </label>

              <label class="field">
                <span>Alt text (optional, also guides the model's slot pick)</span>
                <input class="form-field" type="text" v-model="stockAdminAltText" placeholder="e.g. Lush landscaped garden with stone path" />
              </label>

              <label class="admin-action admin-action--primary stock-upload-btn">
                <v-progress-circular v-if="stockAdminUploading" indeterminate size="15" width="2" />
                <template v-else>
                  <v-icon size="15">mdi-cloud-upload-outline</v-icon>
                  Upload photo
                </template>
                <input
                  ref="stockAdminFileInput"
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  class="stock-upload-input"
                  :disabled="stockAdminUploading"
                  @change="handleStockAdminFileInput"
                />
              </label>
            </template>
          </div>

          <div v-if="stockAdminUploadError" class="admin-error">{{ stockAdminUploadError }}</div>
          <div v-if="stockAdminError" class="admin-error">{{ stockAdminError }}</div>

          <div v-if="!stockAdminIndustryKey" class="list-empty">
            <v-icon size="26" color="var(--text-secondary)">mdi-image-multiple-outline</v-icon>
            <span>Select an industry to manage its stock photos</span>
          </div>
          <div v-else-if="stockAdminLoading" class="list-empty">
            <v-progress-circular indeterminate size="20" width="2" />
            <span>Loading…</span>
          </div>
          <div v-else-if="!stockAdminImages.length" class="list-empty">
            <v-icon size="26" color="var(--text-secondary)">mdi-image-off-outline</v-icon>
            <span>No stock photos for this industry yet</span>
          </div>
          <div v-else class="stock-media-grid">
            <div v-for="image in stockAdminImages" :key="image.id" class="stock-media-tile">
              <img :src="image.thumbnail_url || image.url" :alt="image.alt_text || image.filename" loading="lazy" />
              <div class="stock-media-tile-meta">
                <span class="stock-media-tile-slot">{{ slotLabel(image.slot) }}</span>
                <span class="stock-media-tile-alt">{{ image.alt_text || '—' }}</span>
              </div>
              <button
                type="button"
                class="stock-media-tile-delete"
                :disabled="stockAdminDeleting === image.id"
                :title="stockAdminDeleting === image.id ? 'Deleting…' : 'Delete'"
                @click="deleteStockAdminImage(image)"
              >
                <v-icon size="14">{{ stockAdminDeleting === image.id ? 'mdi-loading mdi-spin' : 'mdi-trash-can-outline' }}</v-icon>
              </button>
            </div>
          </div>
        </template>

        <!-- ── Domain reservations: beta reserve-and-fulfill flow (see backend/routes/domainReservations.js) ── -->
        <template v-else-if="selectedAdmin === '/admin/domain-reservations'">
          <div class="admin-toolbar">
            <div>
              <strong>Domain Reservations</strong>
              <span>Register/connect these by hand on Cloudflare, then mark fulfilled to add them to the contractor's Domain Management.</span>
            </div>
          </div>

          <div v-if="domainReservationsError" class="admin-error">{{ domainReservationsError }}</div>

          <div v-if="domainReservationsLoading" class="list-empty">
            <v-progress-circular indeterminate size="20" width="2" />
            <span>Loading…</span>
          </div>
          <div v-else-if="!domainReservations.length" class="list-empty">
            <v-icon size="26" color="var(--text-secondary)">mdi-earth-arrow-right</v-icon>
            <span>No pending reservations</span>
          </div>
          <div v-else class="admin-table-wrap">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Action</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Requested</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in domainReservations" :key="r.id">
                  <td><strong>{{ r.domain }}</strong></td>
                  <td>{{ r.action === 'register' ? 'Register' : 'Connect' }}</td>
                  <td>{{ r.company_id }}</td>
                  <td>
                    <template v-if="r.registrant_contact">
                      {{ r.registrant_contact.first_name }} {{ r.registrant_contact.last_name }} — {{ r.registrant_contact.email }}
                      <span v-if="r.contact_extensions?.ca_legal_type"> · CA: {{ r.contact_extensions.ca_legal_type }}</span>
                    </template>
                    <span v-else class="hint">—</span>
                  </td>
                  <td>{{ new Date(r.created_at).toLocaleString() }}</td>
                  <td>
                    <button
                      class="admin-action admin-action--primary"
                      type="button"
                      :disabled="domainReservationActing === r.id"
                      @click="fulfillDomainReservation(r)"
                    >
                      Mark fulfilled
                    </button>
                    <button
                      class="admin-action"
                      type="button"
                      :disabled="domainReservationActing === r.id"
                      @click="cancelDomainReservation(r)"
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- ── Admins: flat table ── -->
        <template v-else>
          <div class="admin-toolbar">
            <div>
              <strong>Platform Admins</strong>
              <span>{{ admins.length }} records</span>
            </div>
            <button class="admin-action admin-action--primary" type="button" @click="addAdminDialog = true">
              <v-icon size="16">mdi-plus</v-icon> Add Admin
            </button>
          </div>
          <div class="admin-table-wrap">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>Firebase UID</th>
                  <th>Email</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Created</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody v-if="adminRows.length">
                <tr v-for="(row, i) in adminRows" :key="i">
                  <td>{{ row['Firebase UID'] }}</td>
                  <td>{{ row.Email }}</td>
                  <td>{{ row['First Name'] }}</td>
                  <td>{{ row['Last Name'] }}</td>
                  <td>{{ row.Created }}</td>
                  <td class="col-actions">
                    <button class="row-btn row-btn--danger" type="button" title="Delete" @click="openDeleteAdmin(row._raw)">
                      <v-icon size="13">mdi-trash-can-outline</v-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr><td class="empty-cell" colspan="6">No admins found.</td></tr>
              </tbody>
            </table>
          </div>
        </template>

      </section>
    </section>
  </main>

  <!-- Edit dialog -->
  <v-dialog v-model="editDialog" max-width="480">
    <v-card>
      <v-card-title class="dialog-title">Edit {{ editSection === 'user' ? 'User' : 'Company' }}</v-card-title>
      <v-card-text>
        <div v-if="editError" class="dialog-error">{{ editError }}</div>
        <template v-if="editSection === 'user'">
          <v-text-field v-model="editForm.first_name" label="First Name" variant="outlined" density="compact" class="mb-2" />
          <v-text-field v-model="editForm.last_name"  label="Last Name"  variant="outlined" density="compact" class="mb-2" />
          <v-text-field v-model="editForm.email"      label="Email"      variant="outlined" density="compact" class="mb-2" />
          <v-select v-model="editForm.role" label="Role" :items="['owner', 'admin', 'staff']" variant="outlined" density="compact" />
        </template>
        <template v-else>
          <v-text-field v-model="editForm.company_name"      label="Company Name"  variant="outlined" density="compact" class="mb-2" />
          <v-text-field v-model="editForm.company_email"     label="Company Email" variant="outlined" density="compact" class="mb-2" />
          <v-text-field v-model="editForm.company_phone"     label="Company Phone" variant="outlined" density="compact" class="mb-2" />
          <v-text-field v-model="editForm.main_contact_name" label="Main Contact"  variant="outlined" density="compact" class="mb-2" />
          <v-text-field v-model="editForm.industry"          label="Industry"      variant="outlined" density="compact" class="mb-2" />
          <v-text-field v-model="editForm.sub_industry"      label="Sub Industry"  variant="outlined" density="compact" />
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <button class="dialog-btn" @click="editDialog = false">Cancel</button>
        <button class="dialog-btn dialog-btn--primary" :disabled="editSaving" @click="saveEdit">
          {{ editSaving ? 'Saving…' : 'Save' }}
        </button>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete dialog -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title class="dialog-title">Confirm Delete</v-card-title>
      <v-card-text>
        <div v-if="deleteError" class="dialog-error">{{ deleteError }}</div>
        <p>Delete <strong>{{ deleteTarget?.email || deleteTarget?.firebase_uid }}</strong>? This cannot be undone.</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <button class="dialog-btn" @click="deleteDialog = false">Cancel</button>
        <button class="dialog-btn dialog-btn--danger" :disabled="deleteDeleting" @click="confirmDelete">
          {{ deleteDeleting ? 'Deleting…' : 'Delete' }}
        </button>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Add Admin dialog -->
  <v-dialog v-model="addAdminDialog" max-width="480">
    <v-card>
      <v-card-title class="dialog-title">Add Platform Admin</v-card-title>
      <v-card-text>
        <div v-if="addAdminError" class="dialog-error">{{ addAdminError }}</div>
        <v-text-field v-model="addAdminForm.firebase_uid" label="Firebase UID" variant="outlined" density="compact" class="mb-2" />
        <v-text-field v-model="addAdminForm.email"        label="Email"        variant="outlined" density="compact" class="mb-2" />
        <v-text-field v-model="addAdminForm.first_name"   label="First Name"   variant="outlined" density="compact" class="mb-2" />
        <v-text-field v-model="addAdminForm.last_name"    label="Last Name"    variant="outlined" density="compact" />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <button class="dialog-btn" @click="addAdminDialog = false">Cancel</button>
        <button class="dialog-btn dialog-btn--primary" :disabled="addAdminSaving" @click="submitAddAdmin">
          {{ addAdminSaving ? 'Adding…' : 'Add Admin' }}
        </button>
      </v-card-actions>
    </v-card>
  </v-dialog>








<ImagePickerModal
  v-model="imagePickerOpen"
  :images="tenantImages"
  :loading="tenantImagesLoading"
  :uploading="tenantImagesUploading"
  :upload-error="tenantImagesUploadError"
  :selected-url="imagePickerSelectedUrl"
  :deletable="true"
  :deleting="tenantImageDeleting"
  title="Choose image"
  subtitle="Select or upload an image."
  @select="handleImageSelect"
  @upload="uploadTenantImage"
  @delete="deleteTenantImage"
/>

</template>

<style scoped>
.admin-page {
  height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.admin-shell {
  width: 100%;
  margin: 0 auto;
}

.admin-panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.06);
  overflow: hidden;
}

/* ── Tab bar ── */
.tab-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--surface-alt);
}

.tabs {
  display: flex;
  gap: 2px;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 13px 14px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  font: inherit;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.tab:hover {
  color: var(--text-primary);
}

.tab--active {
  color: #1f5c4d;
  border-bottom-color: #1f5c4d;
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #e8f4f0;
  border-radius: 20px;
  color: #1f5c4d;
  font-size: 0.72rem;
  font-weight: 900;
}

.tab--active .tab-count {
  background: #1f5c4d;
  color: #fff;
}

.tab-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.tab-refresh:hover { background: var(--border-subtle); }
.tab-refresh:disabled { cursor: progress; opacity: 0.55; }

/* ── Error bar ── */
.admin-error {
  margin: 12px 16px;
  padding: 10px 12px;
  color: #9f2a1d;
  background: #fff3f1;
  border: 1px solid #ffd5cf;
  border-radius: 6px;
  font-size: 0.86rem;
  font-weight: 700;
}

/* ── Master-detail ── */
.master-detail {
  display: flex;
  height: clamp(480px, calc(100dvh - 240px), 820px);
}

/* LEFT: list panel */
.list-panel {
  width: 272px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-subtle);
  background: var(--surface-alt);
  overflow-y: auto;
}

.search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.search-icon {
  color: var(--text-secondary);
  flex-shrink: 0;
  margin-right: 7px;
}

.search-input {
  flex: 1;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.86rem;
  color: var(--text-primary);
  outline: none;
  min-width: 0;
}

.search-input::placeholder { color: var(--text-secondary); }

.search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: var(--border-subtle);
  border: none;
  border-radius: 50%;
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;
}

.list-toggle {
  display: flex;
  gap: 5px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.toggle-btn {
  flex: 1;
  padding: 5px 8px;
  background: none;
  border: 1px solid var(--border-subtle);
  border-radius: 5px;
  color: var(--text-secondary);
  font: inherit;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}

.toggle-btn--active {
  background: #1f5c4d;
  border-color: #1f5c4d;
  color: #fff;
}

.list-meta {
  padding: 6px 12px 4px;
  flex-shrink: 0;
}

.list-meta-note {
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.list-meta-note--search {
  color: #2169c6;
}

.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  color: var(--text-secondary);
  font-size: 0.84rem;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  background: none;
  border: none;
  border-left: 2px solid transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s, border-color 0.12s;
}

.list-item:hover { background: var(--surface-alt); }

/* #eef8f5 is a fixed light tint, not theme-reactive — so .list-name/.list-sub
   inside can't stay on var(--text-primary)/var(--text-secondary) either, or
   they go near-white on a light background in dark mode. */
.list-item--active {
  background: #eef8f5;
  border-left-color: #1f5c4d;
}

.list-item--active .list-name {
  color: #17231f;
}

.list-item--active .list-sub {
  color: #56635e;
}

.list-avatar,
.team-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 900;
  flex-shrink: 0;
  color: #fff;
}

.detail-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 1rem;
  font-weight: 900;
  flex-shrink: 0;
  color: #fff;
}

.avatar-owner { background: #1f5c4d; }
.avatar-admin { background: #2169c6; }
.avatar-staff { background: #667085; }

.list-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.list-name {
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-sub {
  font-size: 0.76rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* RIGHT: detail panel */
.detail-panel {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
}

.detail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  color: var(--text-secondary);
  font-size: 0.88rem;
}

.detail-section {
  padding: 18px 20px;
}

.detail-divider {
  height: 1px;
  background: var(--border-subtle);
}

/* User header */
.user-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.user-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-header-right {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.detail-name {
  margin: 0 0 3px;
  font-size: 1.04rem;
  font-weight: 850;
  color: var(--text-primary);
}

.detail-email {
  font-size: 0.84rem;
  color: var(--text-secondary);
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.82rem;
  color: var(--text-secondary);
}

.meta-label {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 850;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.meta-code {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.78rem;
  color: var(--text-secondary);
  background: var(--surface-alt);
  padding: 2px 5px;
  border-radius: 3px;
}

/* Section head */
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.section-label-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-heading {
  font-size: 0.84rem;
  font-weight: 850;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.section-acts {
  display: flex;
  align-items: center;
  gap: 6px;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--border-subtle);
  border-radius: 20px;
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 900;
}

/* Info grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px 20px;
}

.info-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 0.86rem;
  color: var(--text-primary);
}

.detail-none {
  font-size: 0.84rem;
  color: var(--text-secondary);
  margin: 0;
}

/* Team list */
.team-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.team-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 6px;
  background: var(--surface-alt);
  border: 1px solid var(--border-subtle);
}

.team-avatar {
  width: 28px;
  height: 28px;
  font-size: 0.66rem;
}

.team-info {
  flex: 1;
  min-width: 0;
}

.team-name {
  display: block;
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.team-email {
  display: block;
  font-size: 0.75rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.team-acts {
  display: flex;
  gap: 5px;
  flex-shrink: 0;
}

/* Role chips */
.role-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.role-chip--sm {
  font-size: 0.66rem;
  padding: 2px 6px;
}

.role-owner { background: #eef8f5; color: #1f5c4d; }
.role-admin  { background: #eff6ff; color: #2169c6; }
.role-staff  { background: var(--surface-alt); color: var(--text-secondary); }

/* Action buttons */
.act-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 30px;
  padding: 0 10px;
  background: var(--surface-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-secondary);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s;
}

.act-btn:hover { background: var(--surface-alt); }

.act-btn--danger { color: #9f2a1d; }
.act-btn--danger:hover { background: #fff3f1; border-color: #ffd5cf; }

.act-btn--cms { color: #2169c6; }
.act-btn--cms:hover { background: #eff6ff; border-color: #bfdbfe; }

/* Row buttons (icon-only) */
.row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 28px;
  padding: 0 8px;
  background: var(--surface-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 5px;
  color: var(--text-secondary);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s;
}

.row-btn:hover { background: var(--surface-alt); }
.row-btn--danger { color: #9f2a1d; }
.row-btn--danger:hover { background: #fff3f1; border-color: #ffd5cf; }

/* ── Companies toolbar ── */
.co-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--surface-alt);
}

.co-search-wrap {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  padding: 0 10px;
}

.co-search-wrap .search-input {
  flex: 1;
  padding: 8px 4px;
}

.co-count {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary);
  white-space: nowrap;
}

.co-name {
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--text-primary);
}

.co-sub {
  font-size: 0.78rem;
  color: var(--text-secondary);
}

.co-domain {
  font-size: 0.84rem;
  font-weight: 600;
  color: #1f5c4d;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.row-btn--cms {
  color: #2169c6;
}

.row-btn--cms:hover {
  background: #eff6ff;
  border-color: #bfdbfe;
}

/* ── Admins flat table (unchanged design) ── */
.admin-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-subtle);
}

.admin-toolbar strong,
.admin-toolbar span { display: block; }
.admin-toolbar span { margin-top: 2px; color: var(--text-secondary); font-size: 0.82rem; }

.admin-action {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 0 13px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: #2169c6;
  font: inherit;
  font-weight: 850;
  font-size: 0.86rem;
  cursor: pointer;
}

.admin-action--primary { background: #1f5c4d; border-color: #1f5c4d; color: #fff; }

.admin-table-wrap {
  overflow-x: auto;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.admin-table th,
.admin-table td {
  padding: 11px 16px;
  border-bottom: 1px solid var(--border-subtle);
  text-align: left;
  white-space: nowrap;
}

.admin-table th {
  color: var(--text-secondary);
  background: var(--surface-alt);
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.admin-table tr:last-child td { border-bottom: 0; }
.empty-cell { color: var(--text-secondary); text-align: center !important; }
.col-actions { width: 1%; }

/* ── Database explorer ── */
.db-sortable-th {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.db-sortable-th:hover { color: var(--text-primary); }

.db-json-toggle {
  padding: 2px 8px;
  background: var(--surface-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 5px;
  color: #1f5c4d;
  font: inherit;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
}

.db-json-pre {
  margin-top: 6px;
  padding: 8px 10px;
  max-width: 480px;
  max-height: 260px;
  overflow: auto;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 6px;
  font-size: 0.76rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.db-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 14px;
}

.db-pager-label {
  font-size: 0.84rem;
  color: var(--text-secondary);
  font-weight: 700;
}

/* ── Dialogs ── */
.dialog-title {
  font-size: 1rem !important;
  font-weight: 850 !important;
  padding: 16px 20px 8px !important;
}

.dialog-error {
  margin-bottom: 12px;
  padding: 8px 10px;
  color: #9f2a1d;
  background: #fff3f1;
  border: 1px solid #ffd5cf;
  border-radius: 6px;
  font-size: 0.84rem;
  font-weight: 700;
}

.dialog-btn {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 14px;
  background: var(--surface-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-primary);
  font: inherit;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
}

.dialog-btn--primary { background: #1f5c4d; border-color: #1f5c4d; color: #fff; }
.dialog-btn--danger  { background: #9f2a1d; border-color: #9f2a1d; color: #fff; }
.dialog-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Activity log (per-user detail panel) ── */
.activity-loading {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.activity-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 5px;
  background: var(--surface-alt);
  border: 1px solid var(--border-subtle);
}

.activity-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 12px;
  font-size: 0.68rem;
  font-weight: 900;
  white-space: nowrap;
  flex-shrink: 0;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

.activity-label {
  flex: 1;
  min-width: 0;
  font-size: 0.82rem;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-time {
  font-size: 0.74rem;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Global logs tab filters ── */
.log-select {
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.84rem;
  cursor: pointer;
  outline: none;
}

.log-date {
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.84rem;
  outline: none;
  width: 140px;
}

/* ── Template Builder ── */
.tpl-builder {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 24px;
  height: calc(100vh - 260px);
}
.tpl-editor-col,
.tpl-preview-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}
.tpl-label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: var(--text-secondary);
}
.tpl-textarea {
  flex: 1;
  width: 100%;
  padding: 14px;
  font-family: 'Fira Code', 'Cascadia Code', monospace;
  font-size: 0.8rem;
  line-height: 1.6;
  border: 1.5px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  resize: none;
  outline: none;
}
.tpl-textarea:focus {
  border-color: #6366f1;
}
.tpl-actions {
  display: flex;
  gap: 8px;
}
.tpl-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.83rem;
}
.tpl-success {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  color: #16a34a;
  font-size: 0.83rem;
}
.tpl-thumbnail-row {
  padding: 14px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tpl-thumbnail-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tpl-thumbnail-preview {
  width: 200px;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-alt);
}
.tpl-thumbnail-input-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.tpl-thumbnail-input-row .tpl-meta-input {
  flex: 1;
  min-width: 0;
}
.tpl-thumbnail-empty {
  font-size: 0.82rem;
  color: var(--text-secondary);
}
.tpl-load-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.tpl-load-select {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  font-size: 0.88rem;
  color: var(--text-primary);
  background: var(--surface);
  cursor: pointer;
  outline: none;
}
.tpl-load-select:focus {
  border-color: #2f6fed;
  box-shadow: 0 0 0 2px rgba(47, 111, 237, .15);
}
.tpl-load-divider {
  font-size: 0.82rem;
  color: var(--text-secondary);
  white-space: nowrap;
}
.tpl-meta-fields {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}
.tpl-meta-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tpl-meta-input {
  padding: 7px 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  font-size: 0.88rem;
  color: var(--text-primary);
  background: var(--surface);
  outline: none;
}
.tpl-meta-input:focus {
  border-color: #2f6fed;
  box-shadow: 0 0 0 2px rgba(47, 111, 237, .15);
}
.tpl-meta-input:disabled {
  background: var(--surface-alt);
  color: var(--text-secondary);
  cursor: not-allowed;
}
.tpl-meta-required {
  color: #c0392b;
  font-weight: 700;
}
.tpl-label-sub {
  font-weight: 400;
  color: var(--text-secondary);
  font-size: 0.78rem;
  margin-left: 4px;
}
.tpl-preview-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1.5px dashed var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 0.85rem;
}
.tpl-preview-iframe {
  flex: 1;
  width: 100%;
  border: 1.5px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
}

/* ── Print Templates tab ── */
.pt-new-btn {
  width: 100%;
  justify-content: center;
  margin-bottom: 12px;
}

.pt-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  align-items: start;
}

.pt-form {
  display: grid;
  gap: 14px;
}

.pt-form-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.field {
  display: grid;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary);
}

.form-field {
  height: 38px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.86rem;
  outline: none;
}

.pt-textarea {
  height: auto;
  padding: 8px 10px;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  resize: vertical;
}

.pt-form-actions {
  display: flex;
  gap: 10px;
}

.pt-delete-btn {
  color: #9f2a1d;
  border-color: #9f2a1d;
}

.pt-preview {
  position: sticky;
  top: 0;
  display: grid;
  gap: 8px;
  justify-items: center;
}

.pt-preview-label {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-align: center;
}

.pt-preview-canvas {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border-subtle);
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(23, 35, 31, 0.1);
  max-width: 100%;
}

/* ── Stock Photos ── */
.stock-media-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-subtle);
}

.stock-media-controls .field { min-width: 200px; }

.stock-upload-btn {
  position: relative;
  cursor: pointer;
}

.stock-upload-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.stock-media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
  padding: 16px;
}

.stock-media-tile {
  position: relative;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  overflow: hidden;
  background: var(--surface);
}

.stock-media-tile img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.stock-media-tile-meta {
  padding: 8px;
  display: grid;
  gap: 2px;
}

.stock-media-tile-slot {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1f5c4d;
}

.stock-media-tile-alt {
  font-size: 0.78rem;
  color: var(--text-secondary);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.stock-media-tile-delete {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background: rgba(255, 255, 255, 0.92);
  color: #c0392b;
  cursor: pointer;
}

.stock-media-tile-delete:disabled {
  color: var(--text-secondary);
  cursor: wait;
}
</style>
